export type PosterTextField =
  | 'title'
  | 'subtitle'
  | 'intro'
  | 'addressHeader'
  | 'companyHeader'
  | 'cta'
  | 'welcomeHeadline'
  | 'welcomeSubheading'
  | 'checkInInfo'
  | 'wifiSnippet'
  | 'houseRulesTeaser';

export interface PosterTextSet {
  title: string;
  subtitle: string;
  intro: string;
  addressHeader: string;
  companyHeader: string;
  cta: string;
  welcomeHeadline: string;
  welcomeSubheading: string;
  checkInInfo: string;
  wifiSnippet: string;
  houseRulesTeaser: string;
}

export type QrPosterTexts = Record<string, PosterTextSet>;

export const DEFAULT_QR_POSTER_TEXTS: QrPosterTexts = {
  de: {
    title: 'NOTFALL AUSHANG',
    subtitle: 'Wichtige Kontaktinformationen auf einen SCAN',
    intro: 'Scannen Sie den QR-Code, um schnell alle wichtigen Kontaktdaten, Dienstleistungen und Notfallnummern für dieses Gebäude zu erhalten.',
    addressHeader: 'Objektadresse',
    companyHeader: 'Firmeninformationen',
    cta: 'Hier scannen',
    welcomeHeadline: 'Willkommen in Ihrer Unterkunft',
    welcomeSubheading: 'Scannen Sie den QR-Code für Ihren digitalen Gästeguide',
    checkInInfo: 'Check-in ab 15:00 Uhr · Check-out bis 11:00 Uhr',
    wifiSnippet: 'WLAN-Zugangsdaten & mehr.',
    houseRulesTeaser: 'Hausregeln und Infos auf einen Blick.'
  },
  en: {
    title: 'EMERGENCY POSTER',
    subtitle: 'Essential contact information at one scan',
    intro: 'Scan the QR code to quickly access all important contact details, services, and emergency numbers for this building.',
    addressHeader: 'Property address',
    companyHeader: 'Company information',
    cta: 'Scan here',
    welcomeHeadline: 'Welcome to your stay',
    welcomeSubheading: 'Scan the QR code for your digital guest guide',
    checkInInfo: 'Check-in from 3 pm · Check-out until 11 am',
    wifiSnippet: 'Wi-Fi details and more.',
    houseRulesTeaser: 'House rules, tips, and key contacts.'
  }
};

export const QR_POSTER_TEXT_LIMITS: Record<PosterTextField, { soft: number; hard: number }> = {
  title: { soft: 20, hard: 40 },
  subtitle: { soft: 50, hard: 80 },
  intro: { soft: 135, hard: 155 },
  addressHeader: { soft: 20, hard: 40 },
  companyHeader: { soft: 20, hard: 40 },
  cta: { soft: 15, hard: 20 },
  welcomeHeadline: { soft: 35, hard: 60 },
  welcomeSubheading: { soft: 80, hard: 100 },
  checkInInfo: { soft: 80, hard: 140 },
  wifiSnippet: { soft: 40, hard: 50 },
  houseRulesTeaser: { soft: 40, hard: 50 }
};

export function normalizePosterTexts(texts?: QrPosterTexts | null): QrPosterTexts {
  const normalized: QrPosterTexts = {};
  const languageKeys = new Set([
    ...Object.keys(DEFAULT_QR_POSTER_TEXTS),
    ...Object.keys(texts || {})
  ]);

  languageKeys.forEach((langKey) => {
    const key = langKey.toLowerCase();
    const defaults = DEFAULT_QR_POSTER_TEXTS[key] ?? DEFAULT_QR_POSTER_TEXTS['de'];
    const source = (texts?.[langKey] || texts?.[key]) as Partial<PosterTextSet> | undefined;

    normalized[key] = {
      title: source?.title ?? defaults.title,
      subtitle: source?.subtitle ?? defaults.subtitle,
      intro: source?.intro ?? defaults.intro,
      addressHeader: source?.addressHeader ?? defaults.addressHeader,
      companyHeader: source?.companyHeader ?? defaults.companyHeader,
      cta: source?.cta ?? defaults.cta,
      welcomeHeadline: source?.welcomeHeadline ?? defaults.welcomeHeadline,
      welcomeSubheading: source?.welcomeSubheading ?? defaults.welcomeSubheading,
      checkInInfo: source?.checkInInfo ?? defaults.checkInInfo,
      wifiSnippet: source?.wifiSnippet ?? defaults.wifiSnippet,
      houseRulesTeaser: source?.houseRulesTeaser ?? defaults.houseRulesTeaser
    };
  });

  return normalized;
}

export function clonePosterTexts(texts: QrPosterTexts): QrPosterTexts {
  const clone: QrPosterTexts = {};
  Object.keys(texts).forEach((lang) => {
    clone[lang] = { ...texts[lang] };
  });
  return clone;
}

export function selectPosterTextSet(texts: QrPosterTexts | null | undefined, language: string): PosterTextSet {
  const normalized = texts ? normalizePosterTexts(texts) : normalizePosterTexts();
  const requested = language?.toLowerCase?.() || '';
  const fallbackOrder = [requested, requested.split('-')[0], 'de', 'en', ...Object.keys(normalized)];
  const selectedLang = fallbackOrder.find((langKey) => langKey && normalized[langKey]);
  const selectedSet = (selectedLang && normalized[selectedLang]) || DEFAULT_QR_POSTER_TEXTS['de'];
  return { ...selectedSet };
}
